import {
  LayoutDashboard,
  Lightbulb,
  Activity,
  BarChart3,
  Video,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { HubShell } from '@/components/layout/HubShell';
import { DashboardPage } from './DashboardPage';
import { SmartSuggestionsPage } from './SmartSuggestionsPage';
import { ActivityFeedPage } from './ActivityFeedPage';
import { ProductivityInsightsPage } from './ProductivityInsightsPage';
import MeetingPreparation from './MeetingPreparation';
import { EmbeddedSkillRail } from '@/components/skill';

/**
 * Home hub — the default landing surface.
 *
 * Tabs:
 * - Overview: full DashboardPage (stats, hero, suggestions, activity).
 * - Suggestions: SmartSuggestionsPage.
 * - Activity: ActivityFeedPage.
 * - Insights: ProductivityInsightsPage.
 * - Meeting Prep: MeetingPreparation.
 *
 * The hub keeps every DashboardPage section intact while making
 * the other home-adjacent pages reachable without leaving Home.
 */
export function HomePage() {
  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      render: () => (
        <div className="space-y-6">
          <QuickActionsBanner />
          <EmbeddedSkillRail
            title="Recommended for you"
            restrictCategories={['analyze', 'optimize']}
          />
          <DashboardPage />
        </div>
      ),
    },
    {
      id: 'suggestions',
      label: 'Suggestions',
      icon: Lightbulb,
      render: () => <SmartSuggestionsPage />,
    },
    {
      id: 'activity',
      label: 'Activity',
      icon: Activity,
      render: () => <ActivityFeedPage />,
    },
    {
      id: 'insights',
      label: 'Insights',
      icon: BarChart3,
      render: () => <ProductivityInsightsPage />,
    },
    {
      id: 'meeting',
      label: 'Meeting Prep',
      icon: Video,
      render: () => <MeetingPreparation />,
    },
  ];

  return (
    <HubShell
      title="Home"
      description="Your day at a glance — tabs, suggestions, activity, and AI help."
      icon={LayoutDashboard}
      tabs={tabs}
      defaultSection="overview"
    />
  );
}

function QuickActionsBanner() {
  const actions = [
    { label: 'Smart Actions', to: '/ai?section=smart-actions', icon: Sparkles, color: 'from-amber-500/30 to-orange-500/30', sub: 'Compound agent workflows' },
    { label: 'Generate App', to: '/apps/generate', icon: Sparkles, color: 'from-violet-500/30 to-fuchsia-500/30', sub: 'Synthesize live tabs' },
    { label: 'Organize Tabs', to: '/tabs', icon: LayoutDashboard, color: 'from-cyan-500/30 to-blue-500/30', sub: 'Entropy & clustering' },
    { label: 'Ask AI Chat', to: '/ai', icon: Lightbulb, color: 'from-primary-500/30 to-emerald-500/30', sub: 'Live workspace RAG' },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {actions.map((a) => {
        const Icon = a.icon;
        return (
          <Link
            key={a.to}
            to={a.to}
            className="group relative flex h-full min-h-[4.5rem] items-center gap-3 overflow-hidden rounded-2xl border border-khiimori-border bg-khiimori-bg-elevated/40 p-4 backdrop-blur-xl transition-all hover:border-primary-500/40 hover:bg-khiimori-bg-elevated/70"
          >
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${a.color}`}>
              <Icon className="h-5 w-5 text-khiimori-text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-khiimori-text-primary">{a.label}</div>
              <div className="text-[11px] text-khiimori-text-secondary/50">{a.sub}</div>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-khiimori-text-secondary/40 transition-transform group-hover:translate-x-1" />
          </Link>
        );
      })}
    </div>
  );
}

export default HomePage;
